import React, { useEffect, useRef } from "react";

/**
 * Interactive headline that reuses the logo's mouse-physics:
 *  - per-letter weight-morph + lift based on cursor proximity
 *  - kinetic light-source shadow cast opposite the cursor
 *  - graceful static fallback on touch devices
 *  - on-load staggered rise reveal (CSS, decoupled from JS transforms)
 *
 * `lines` = array of lines; each line is an array of segments:
 *   { text, accent?: bool, italic?: bool }
 */
export const KineticText = ({ lines = [], className = "", as: Tag = "h1", baseDelay = 0.35 }) => {
  const wrapRef = useRef(null);
  const inners = useRef([]);
  const centers = useRef([]);
  const isTouch = useRef(false);
  const raf = useRef(0);
  const pointer = useRef({ x: 0, y: 0 });

  const measure = () => {
    centers.current = inners.current.map((el) => {
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    });
  };

  useEffect(() => {
    isTouch.current = window.matchMedia("(pointer: coarse)").matches;
    const t = setTimeout(measure, 100);
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", measure, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", measure);
      cancelAnimationFrame(raf.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = () => {
    const { x, y } = pointer.current;
    inners.current.forEach((el, i) => {
      const c = centers.current[i];
      if (!el || !c) return;
      const dist = Math.hypot(x - c.x, y - c.y);
      const influence = Math.max(0, 1 - dist / 160);
      const ty = -influence * 16;
      const scale = 1 + influence * 0.07;
      el.style.transform = `translateY(${ty.toFixed(2)}px) scale(${scale.toFixed(3)})`;
      el.style.fontWeight = String(Math.round(400 + influence * 400));
    });
    if (wrapRef.current) {
      const r = wrapRef.current.getBoundingClientRect();
      const dx = x - (r.left + r.width / 2);
      const dy = y - (r.top + r.height / 2);
      wrapRef.current.style.textShadow = `${(-dx * 0.025).toFixed(1)}px ${(-dy * 0.025).toFixed(1)}px 40px var(--accent-soft)`;
    }
  };

  const handleMove = (e) => {
    if (isTouch.current) return;
    pointer.current = { x: e.clientX, y: e.clientY };
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(apply);
  };

  const handleLeave = () => {
    cancelAnimationFrame(raf.current);
    inners.current.forEach((el) => {
      if (!el) return;
      el.style.transform = "translateY(0) scale(1)";
      el.style.fontWeight = "";
    });
    if (wrapRef.current) wrapRef.current.style.textShadow = "none";
  };

  let g = -1; // global char index for stagger
  inners.current = [];

  return (
    <Tag ref={wrapRef} onMouseMove={handleMove} onMouseLeave={handleLeave} className={className}>
      {lines.map((segments, li) => (
        <span key={li} className="block">
          {segments.map((seg, si) => (
            <span
              key={si}
              className={seg.italic ? "italic" : ""}
              style={seg.accent ? { color: "var(--accent)" } : undefined}
            >
              {seg.text.split("").map((ch) => {
                g += 1;
                const idx = g;
                return (
                  <span
                    key={idx}
                    className="kt-outer"
                    style={{ animationDelay: `${baseDelay + idx * 0.018}s` }}
                  >
                    <span ref={(el) => (inners.current[idx] = el)} className="kt-inner">
                      {ch === " " ? "\u00A0" : ch}
                    </span>
                  </span>
                );
              })}
            </span>
          ))}
        </span>
      ))}
    </Tag>
  );
};

export default KineticText;
